import React from 'react';
import { X, type Icon } from '@phosphor-icons/react';
import { Chip } from './Chip';
import { tonFarben, type Ton } from './ton';

/**
 * Listenzeile in Panels (Prototyp: Icon-Box 40, Titel 14/700, Untertitel, Wert oder Chip rechts).
 * Mit onClick wird die ganze Zeile zum Knopf (z. B. Importzeile ein-/ausschließen).
 */
export function PanelZeile({ icon: IconKomp, ton, titel, sub, rechts, rechtsFarbe, chip, onClick, aktiv = true, ariaLabel, children }: {
  icon: Icon;
  ton: Exclude<Ton, 'glas'>;
  titel: string;
  sub?: string;
  rechts?: React.ReactNode;
  rechtsFarbe?: string;
  chip?: { text: string; ton: Ton };
  onClick?: () => void;
  /** false = abgewählt (gedimmt) */
  aktiv?: boolean;
  ariaLabel?: string;
  /** Zusatzinhalt unter der Zeile (z. B. Eingabefelder) */
  children?: React.ReactNode;
}) {
  const { bg, fg } = tonFarben(ton);
  const flaeche = ton === 'neutral' ? 'var(--fill2)' : bg;
  const farbe = ton === 'neutral' ? 'var(--ink3)' : fg;
  const inhalt = (
    <>
      <span className="flex items-center justify-center shrink-0" style={{ width: 40, height: 40, borderRadius: 12, background: flaeche }}>
        <IconKomp weight="fill" size={17} color={farbe} />
      </span>
      <span className="flex flex-col flex-1 min-w-0">
        <span className="truncate" style={{ fontSize: 14, fontWeight: 700 }}>{titel}</span>
        {sub && <span className="truncate immo-zahl" style={{ fontSize: 12, color: 'var(--ink3)' }}>{sub}</span>}
      </span>
      {rechts !== undefined && <span className="immo-zahl" style={{ fontSize: 14, fontWeight: 800, color: rechtsFarbe ?? 'var(--ink)' }}>{rechts}</span>}
      {chip && <Chip ton={chip.ton} groesse="xs" className="shrink-0">{chip.text}</Chip>}
    </>
  );
  const zeilenStil: React.CSSProperties = { gap: 12, padding: '8px 6px', opacity: aktiv ? 1 : 0.5, transition: 'opacity .2s' };
  return (
    <div className="flex flex-col min-w-0">
      {onClick ? (
        <button type="button" onClick={onClick} aria-label={ariaLabel} aria-pressed={aktiv}
          className="immo-hover-flaeche flex items-center w-full text-left cursor-pointer"
          style={{ ...zeilenStil, borderRadius: 14, border: 'none', background: 'transparent', color: 'inherit' }}>
          {inhalt}
        </button>
      ) : (
        <div className="flex items-center" style={zeilenStil}>{inhalt}</div>
      )}
      {children}
    </div>
  );
}

/**
 * Panel über der Liste (Import, Export): Glas, Kopf mit Icon-Box 40, Titel 20/800,
 * Untertitel und rundem Schließen-Knopf (Prototyp `lp`).
 */
export function ListenPanel({ icon: IconKomp, titel, sub, onSchliessen, children }: {
  icon: Icon; titel: string; sub: string; onSchliessen: () => void; children: React.ReactNode;
}) {
  return (
    <section aria-label={titel} className="immo-glas immo-side-in flex flex-col" style={{ borderRadius: 28, padding: 20, gap: 14 }}>
      <div className="flex items-center" style={{ gap: 12 }}>
        <span className="flex items-center justify-center shrink-0" style={{ width: 40, height: 40, borderRadius: 13, background: 'var(--acc-soft)' }}>
          <IconKomp weight="fill" size={20} color="var(--acc)" />
        </span>
        <div className="flex flex-col flex-1 min-w-0">
          <h2 className="m-0" style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em' }}>{titel}</h2>
          <span style={{ fontSize: 12, color: 'var(--ink3)' }}>{sub}</span>
        </div>
        <button type="button" onClick={onSchliessen} aria-label={`${titel} schließen`}
          className="flex items-center justify-center shrink-0 cursor-pointer immo-hover-flaeche"
          style={{ width: 36, height: 36, borderRadius: '50%', border: 'none', background: 'var(--fill2)', color: 'var(--ink)' }}>
          <X weight="bold" size={14} />
        </button>
      </div>
      {children}
    </section>
  );
}
